import { articleStockKeys } from './useArticleStocks';
import { stockMovementKeys } from './useStockMovements';
import { inventoryArticleKeys } from './useInventoryArticles';
import { invalidateMaterialReports } from './useMaterialReports';
import { materialCostKeys } from './useMaterialCosts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { downloadConsumptionDeliveryPdf } from '../stock-compras/consumptions/utils/consumptionsUtils';

export const consumptionKeys = {
  all: ['consumptions'],
  lists: () => [...consumptionKeys.all, 'list'],
  list: (filters = {}) => [...consumptionKeys.lists(), filters],
  details: () => [...consumptionKeys.all, 'detail'],
  detail: (id) => [...consumptionKeys.details(), id],
  byOrder: (orderId) => [...consumptionKeys.all, 'byOrder', orderId],
};

const buildListParams = (filters = {}) => {
  const params = {};
  const allowedKeys = [
    'status',
    'uuid_external_order',
    'order_number',
    'uuid_warehouse',
    'date_from',
    'date_to',
    'search',
    'page',
    'per_page',
    'sort_by',
    'sort_dir',
  ];

  allowedKeys.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });

  if (filters.uuid_project) params.uuid_project = filters.uuid_project;
  if (filters.uuid_inventory_article) {
    params.uuid_inventory_article = filters.uuid_inventory_article;
  }
  if (filters.destination) params.destination = filters.destination;
  return params;
};

export const useConsumptions = (filters = {}, options = {}) => {
  const params = buildListParams(filters);

  return useQuery({
    queryKey: consumptionKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get('consumptions', { params });
      return response.data;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useConsumptionsByOrder = (orderId, options = {}) => {
  return useConsumptions(
    { uuid_external_order: orderId },
    {
      enabled: !!orderId,
      ...options,
    }
  );
};

export const useConsumption = (id, options = {}) => {
  return useQuery({
    queryKey: consumptionKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`consumptions/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
};

const stripPdfFields = (consumption = {}) => {
  const { pdf_base64, download_name, pdf_error, ...rest } = consumption;
  return rest;
};

const invalidateConsumptionQueries = (queryClient, consumption) => {
  queryClient.invalidateQueries({ queryKey: consumptionKeys.lists() });

  if (consumption?.uuid) {
    queryClient.setQueryData(
      consumptionKeys.detail(consumption.uuid),
      stripPdfFields(consumption)
    );
    queryClient.invalidateQueries({ queryKey: consumptionKeys.detail(consumption.uuid) });
  }

  if (consumption?.uuid_external_order) {
    queryClient.invalidateQueries({
      queryKey: consumptionKeys.byOrder(consumption.uuid_external_order),
    });
    queryClient.invalidateQueries({ queryKey: ['externalOrders'] });
  }
};

export const useGenerateConsumptionFromOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, payload = {} }) => {
      const response = await apiClient.post(
        `consumptions/from-order/${orderId}`,
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: articleStockKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryArticleKeys.all });
      invalidateMaterialReports(queryClient);
      queryClient.invalidateQueries({ queryKey: materialCostKeys.all });
      invalidateConsumptionQueries(queryClient, data);
    },
    onError: (error) => {
      console.error('Error generating consumption from order:', error);
    },
  });
};

export const useConfirmConsumption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload = {} }) => {
      const response = await apiClient.post(`consumptions/${id}/confirm`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      invalidateMaterialReports(queryClient);
      queryClient.invalidateQueries({ queryKey: materialCostKeys.all });
      invalidateConsumptionQueries(queryClient, data);
    },
    onError: (error) => {
      console.error('Error confirming consumption:', error);
    },
  });
};

export const usePrintConsumptionDelivery = () => {
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.get(`consumptions/${id}/print`);
      downloadConsumptionDeliveryPdf(response.data);
      return response.data;
    },
  });
};

export const useDeliverConsumption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const response = await apiClient.post(`consumptions/${id}/deliver`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      invalidateMaterialReports(queryClient);
      queryClient.invalidateQueries({ queryKey: materialCostKeys.all });
      invalidateConsumptionQueries(queryClient, data);
    },
    onError: (error) => {
      console.error('Error delivering consumption:', error);
    },
  });
};

export const useReverseConsumption = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const response = await apiClient.post(`consumptions/${id}/reverse`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: articleStockKeys.all });
      queryClient.invalidateQueries({ queryKey: stockMovementKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryArticleKeys.all });
      invalidateMaterialReports(queryClient);
      queryClient.invalidateQueries({ queryKey: materialCostKeys.all });
      invalidateConsumptionQueries(queryClient, data);
    },
    onError: (error) => {
      console.error('Error reversing consumption:', error);
    },
  });
};
